import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay } from 'swiper/modules';

const introStyles = `
.intro-carousel-swiper,
.intro-carousel-swiper .swiper-slide {
  width: 100%;
  height: 100vh;
}
`;

/**
 * Global reusable full-screen, first-visit-only animated intro — cycles through brand/flavor
 * "hero" slides (crossfade) and then hands off to onComplete once the last slide has played.
 * Props:
 *  - slides: [{ id, background, render() }]
 *  - onComplete(): called once after the last slide finishes (or the user taps "Skip")
 *  - slideDurationMs: how long each slide stays on screen before auto-advancing
 */
const AnimatedIntroCarousel = ({ slides = [], onComplete, slideDurationMs = 2600 } = {}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const completedRef = useRef(false);

  const handleComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete && onComplete();
  };

  useEffect(() => {
    if (slides.length === 0) {
      handleComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (slides.length === 0) return null;

  return (
    <div className="intro-carousel">
      <style>{introStyles}</style>

      <button type="button" className="intro-carousel-skip" onClick={handleComplete}>
        Skip
      </button>

      <Swiper
        modules={[EffectFade, Autoplay]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{ delay: slideDurationMs, disableOnInteraction: false }}
        loop={false}
        allowTouchMove
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        onReachEnd={() => setTimeout(handleComplete, slideDurationMs)}
        className="intro-carousel-swiper"
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <div className="intro-carousel-slide" style={{ background: slide.background }}>
              {slide.render()}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="intro-carousel-dots">
        {slides.map((slide, index) => (
          <span key={slide.id} className={`intro-carousel-dot${index === activeIndex ? ' intro-carousel-dot--active' : ''}`} />
        ))}
      </div>
    </div>
  );
};

export default AnimatedIntroCarousel;
